import type { CSSProperties } from "react";
import { describeElapsed } from "../../lib/labels.js";
import { useElapsed } from "../../lib/useElapsed.js";
import { useSessionStream } from "../../lib/useSessionStream.js";
import { useSiteReady } from "../../lib/useSiteReady.js";
import type { CanvasState } from "../../lib/viewModel.js";
import { useOffersBackend } from "../../lib/xanoHandoff.js";
import { DoneView } from "./DoneView.js";
import { ShellLog } from "./ShellLog.js";
import {
  BuildingEgg,
  HatchScreen,
  Header,
  Nest,
  ParentDino,
  Peak,
  perchOnNest,
  type NestShape,
} from "./Scenery.js";
import { StagePills } from "./StagePills.js";
import { settledPills, stageFor, type Stage } from "./stage.js";

/**
 * Everything from the first activity to the live site.
 *
 * Strictly read-only, for the same reason the classic build view is: the API
 * exposes no route that accepts input into a running session, so the guarantee
 * holds by the absence of surface on both sides rather than by a check.
 *
 * Three things share this component because they are one screen resolving, not
 * three destinations:
 *
 *   building — the egg wobbling and cracking as phases land.
 *
 *   hatching — the build is done and the site is deployed, but the address is
 *   not answering yet. Held here, one word short of the ending, rather than
 *   shown as done. The design already has this beat — a pause before the
 *   reveal — so gating it on the real thing costs no design at all, it just
 *   lets the pause last as long as it actually needs to.
 *
 *   failed — the design specifies nothing for this. Rather than borrowing the
 *   classic skin's card and breaking the world mid-session, it keeps this
 *   layout and stills it: the egg stops struggling, the glow goes out, the
 *   cracks stay where the build got to.
 */
export function BuildView({ sessionId, onRestart }: { sessionId: string; onRestart: () => void }) {
  const stream = useSessionStream(sessionId);
  const elapsed = useElapsed(stream.createdAt, stream.isTerminal);

  const framing = stream.canvas.state === "framing" ? stream.canvas : null;
  const closed = stream.canvas.state === "closed" ? stream.canvas : null;

  // A build that stopped, whichever way it stopped. Derived from the canvas
  // rather than from `outcome`, because a `succeeded` run that produced no site
  // is also over — reading `outcome` alone left the egg wobbling and glowing
  // underneath "It hatched, but empty".
  const stage = stageFor(stream.phase, closed !== null);

  if (framing) {
    // The poll lives in here, and `Reveal` exists only once there is an address
    // to poll for. Calling the hook from this component instead started its 90s
    // give-up clock at session start: every build longer than that arrived at
    // the reveal with the gate already timed out, and the frame mounted with
    // neither the server probe nor the browser rehearsal ever having run — the
    // exact race those two exist to prevent. React has no conditional hooks, so
    // the condition has to be a component boundary.
    return (
      <Reveal
        sessionId={sessionId}
        siteUrl={framing.siteUrl}
        stream={stream}
        stage={stage}
        elapsed={elapsed}
        onRestart={onRestart}
      />
    );
  }

  return (
    <BuildingScreen
      stream={stream}
      stage={stage}
      elapsed={elapsed}
      closed={closed}
      onRestart={onRestart}
    />
  );
}

/**
 * The hold between "deployed" and "reachable".
 *
 * Exists only while there is an address, which is the whole point: mounting it
 * is what starts the readiness poll, so the poll's budget is spent on the wait
 * it was written for rather than on the build that preceded it.
 *
 * While the answer is still no, this keeps showing the building screen on the
 * last beat before the ending — the design's own pause before the reveal, held
 * open for exactly as long as the site takes to start answering, and worded so
 * that it stays a pause rather than becoming the ending. See HATCHING_COPY.
 */
function Reveal({
  sessionId,
  siteUrl,
  stream,
  stage,
  elapsed,
  onRestart,
}: {
  sessionId: string;
  siteUrl: string;
  stream: ReturnType<typeof useSessionStream>;
  stage: Stage;
  elapsed: number | null;
  onRestart: () => void;
}) {
  // Polls the API, which makes a real request and reads a real status code, and
  // then rehearses the frame's own requests from this browser. See
  // lib/useSiteReady.ts — it carries three separate production bugs' worth of
  // reasoning and is deliberately used unchanged.
  const site = useSiteReady(sessionId, siteUrl);

  // Decided in lib so every skin answers it the same way — see
  // lib/xanoHandoff.ts. The done screen only ever learns whether there is a
  // backend, never how to reach it. Read here rather than at the call site
  // below because the `checking` return underneath is an early exit, and a hook
  // may not sit after one.
  const hasBackend = useOffersBackend({
    outcome: stream.outcome,
    xanoTenant: stream.xanoTenant,
  });

  if (site.status === "checking") {
    const waiting = site.waitedMs > 20_000 ? HATCHING_COPY.slow : HATCHING_COPY.normal;
    return (
      <BuildingScreen
        stream={stream}
        stage={stage}
        elapsed={elapsed}
        closed={null}
        onRestart={onRestart}
        // The build is finished; the wait now is the address coming online.
        // Saying so beats a status line that has stopped being true.
        statusTitle={waiting.title}
        statusSub={waiting.sub}
      />
    );
  }

  const terminalAt = stream.steps.find((s) => s.kind === "terminal")?.at ?? null;

  return (
    <DoneView
      siteUrl={siteUrl}
      prompt={stream.prompt}
      // The done screen keeps the building screen's chrome — the same tracker
      // across the top and the same log down the right — so it takes the same
      // three values rather than deriving a second version of any of them.
      pills={settledPills(stage.pills)}
      steps={stream.steps}
      fileCount={stream.fileCount}
      // Measured between the two server timestamps rather than taken from the
      // live clock. `useElapsed` starts counting at mount, which is right while
      // watching a build happen and wrong on the path the session URL exists
      // for: reload onto a finished build half an hour later and it would
      // announce that a four-minute build took thirty-four.
      buildMs={
        terminalAt !== null && stream.createdAt !== null
          ? Math.max(0, terminalAt - stream.createdAt)
          : elapsed
      }
      terminalAt={terminalAt}
      sessionId={sessionId}
      hasBackend={hasBackend}
      // Passed through untouched, absences and all: the screen decides what to
      // draw for a count it does not have, and lib decides nothing here because
      // there is nothing to decide — these are numbers or they are missing.
      objects={stream.xanoObjects}
      sitePages={stream.sitePages}
      onRestart={onRestart}
    />
  );
}

/**
 * What the egg says while it is coming through the shell.
 *
 * `stage.statusTitle` reads "Hatched!" at this point, because the phase really
 * is `done` — the build finished and the deploy reported an address. But the
 * address is not answering yet, and "Hatched!" is the word this skin spends on
 * the moment the site appears. Saying it here spends it early, on a screen with
 * no site on it, and then the actual arrival has nothing left to announce: the
 * user reads the peak, waits through it, and gets the payoff after the applause.
 *
 * So this beat is held one notch short of the claim. The egg is at four cracks,
 * glowing, shuddering on the fastest wobble — everything visual is already at
 * its loudest — and the words are the last thing still withholding. "Hatched!"
 * belongs to `DoneView`'s "It hatched!", where there is a site under it.
 *
 * The slow variant is not a fallback for an error. Past twenty seconds the same
 * unresolved sentence starts reading as a stuck screen rather than a held
 * breath, so it changes to admit the wait while promising the same ending.
 */
const HATCHING_COPY = {
  normal: {
    title: "It's hatching!",
    sub: "The shell is giving way — catching its breath while the address comes online.",
  },
  slow: {
    title: "Any second now…",
    sub: "The nest is warm but the address is taking its time. Almost through.",
  },
} as const;

/**
 * The stage tracker across the top, the scenery on one side, the build log on
 * the other — every state that is not the reveal.
 *
 * The two panes are not decoration and content. They are the same build read
 * two ways: the left says how it feels, the right says what it is doing, and a
 * user watching a four-minute build wants both without choosing. That is the
 * whole reason the egg moved out of the middle of the screen — a centred egg
 * left a seven-line card underneath it and the rest of the build's history on
 * the floor.
 *
 * Stacked below the breakpoint with the log on top, the same way the classic
 * build view stacks: the scenery is anchored to the base of its column, and a
 * column at the base of the screen is the only place the mountain can stand
 * without floating above the cloud sea, which is fixed to the bottom of the
 * viewport at every size.
 */
function BuildingScreen({
  stream,
  stage,
  elapsed,
  closed,
  onRestart,
  statusTitle,
  statusSub,
}: {
  stream: ReturnType<typeof useSessionStream>;
  stage: Stage;
  elapsed: number | null;
  closed: Extract<CanvasState, { state: "closed" }> | null;
  onRestart: () => void;
  /** Overrides the phase's own heading and line — see HATCHING_COPY. */
  statusTitle?: string;
  statusSub?: string;
}) {
  return (
    <HatchScreen fill>
      <Header prompt={stream.prompt}>
        <span className="shrink-0 font-plex text-[13px] text-bark-faint">
          {!stream.isTerminal && stream.connection === "reconnecting"
            ? // Quiet by design: a brief drop during a long build is normal and
              // an error banner would alarm the user about a self-healing gap.
              "Reconnecting…"
            : elapsed !== null
              ? `${describeElapsed(elapsed)} in the shell`
              : ""}
        </span>
      </Header>

      <StagePills pills={stage.pills} />

      {/*
        `min-h-0` on the row and on each pane is load-bearing. A flex child
        defaults to a content-derived minimum size and refuses to shrink below
        it, which makes the log's `overflow-y-auto` do nothing at all and turns
        the frame back into an unbounded page.

        `flex-col-reverse` below the breakpoint puts the log on top and the
        scenery at the base of the screen — see the note on this component.
      */}
      <main className="flex min-h-0 flex-1 flex-col-reverse lg:flex-row">
        {/*
          The scenery column. `relative` is what the peak is centred on: it is
          absolutely positioned at this column's base rather than fixed to the
          viewport, so it cannot reach across into the pane beside it however
          wide the peak is drawn.

          `.hatch-scene` reserves the height the scenery occupies so the status
          text stops where the mountain starts. The reserve tracks `--peak-scale`,
          the same ladder the peak is drawn at — a flat number would hold back
          120px of nothing on a phone, where the peak renders at 0.68.
        */}
        <section
          // `grow` matters only when the log is gone: with both panes present
          // their bases already sum to the whole, so there is no free space for
          // it to distribute and the stacked split stays even. Alone, it is what
          // lets the scenery take the screen rather than half of it.
          /*
            `justify-end` sits the status text just above the egg's crown rather
            than centring it in the space above the scenery, which left a couple
            of hundred pixels of empty paper between the sentence and the thing
            it is a sentence about. `.hatch-scene`'s reserve is what it stops
            against, so the two move together at every peak size.

            Stacked, this band is its content's height and the log takes what is
            left — not an even split. An even split is a guess, and on a 667px
            phone it guessed 33px short: the reserve plus three lines of status
            came to more than half the screen, and the overflow rode up over the
            log's card. Sized to its content the band cannot be too small for
            what is in it, and the peak's own scale ladder is what keeps it from
            taking the whole screen on a short one.

            `grow` only when the log is gone, so a stopped build's message has
            the screen rather than a strip at the bottom of it.
          */
          className={`hatch-scene relative flex min-h-0 flex-col items-center justify-end px-5 sm:px-6 lg:basis-0 lg:grow-[5] ${
            closed ? "grow" : "shrink-0 grow-0 basis-auto"
          }`}
          style={{ "--scene-crown": `${EGG_CROWN}px` } as CSSProperties}
        >
          <Peak
            width={PEAK_WIDTH}
            height={330}
            mountainHeight={210}
            capWidth={230}
            capHeight={88}
            capBottom={126}
            placement="page"
          >
            <Nest {...NEST} />
            {/*
              Behind the egg, standing on the nest's right shoulder — the egg is
              the thing being watched and nothing may cross in front of it,
              least of all while it is cracking.
            */}
            <ParentDino {...perchOnNest(NEST, 0.78)} scale={0.86} headTilt={-34} />
            <BuildingEgg
              crackLevel={stage.crackLevel}
              animation={stage.eggAnimation}
              glow={stage.glow}
            />
          </Peak>

          {/* Above the peak in paint order as well as on screen: the peak is a
              later sibling at `z-0`, so without a stacking position of its own
              this block would end up behind the mountain. */}
          <div className="relative z-[2] flex flex-col items-center gap-[7px] text-center">
            <h1 className="font-chewy text-[clamp(26px,4vw,34px)] text-bark">
              {closed ? headingFor(closed.outcome) : (statusTitle ?? stage.statusTitle)}
            </h1>
            <p className="max-w-[460px] text-[15px] font-semibold text-pretty text-bark-soft">
              {closed
                ? (closed.message ?? "Something went wrong on our side.")
                : (statusSub ?? stage.statusSub)}
            </p>
            <button
              type="button"
              onClick={onRestart}
              className="mt-1.5 cursor-pointer font-nunito text-[13px] font-extrabold text-bark-faint transition-colors hover:text-bark-soft"
            >
              {closed ? "Back to the nest" : "Abandon the nest"}
            </button>
          </div>
        </section>

        {/* A stopped build has nothing further to report, and a log frozen
            mid-sentence beside a failure message reads as a build still
            thinking about it. */}
        {!closed && (
          // `z-[2]` puts the pane in front of the cloud sea, which is fixed to
          // the bottom 70px of every screen in this skin and would otherwise be
          // painted across the last two rows of the build log. The sea belongs
          // to the scenery; running it behind the pane costs the log none of its
          // height and still leaves the mountain standing in the water.
          <div className="relative z-[2] flex min-h-0 flex-1 flex-col p-4 lg:basis-0 lg:grow-[4] lg:py-6 lg:pr-6 lg:pl-0">
            <ShellLog
              steps={stream.steps}
              isTerminal={stream.isTerminal}
              fileCount={stream.fileCount}
            />
          </div>
        )}
      </main>
    </HatchScreen>
  );
}


/**
 * The nest under the big egg, in `Peak` coordinates.
 *
 * One object so the egg, the nest, and the parent beside it cannot drift apart —
 * `perchOnNest` reads the same numbers the nest draws itself from.
 */
const NEST: NestShape = {
  outerWidth: 246,
  outerHeight: 66,
  outerBottom: 186,
  innerWidth: 198,
  innerHeight: 48,
  innerBottom: 196,
  twigScale: 1.2,
};

/**
 * The top of the egg, in `Peak` coordinates — the highest point in the scenery,
 * and so the height the scenery column has to keep clear above it.
 * `BuildingEgg` sits at `bottom: 206` and stands 160px tall.
 *
 * Measured to the egg rather than to the peak group's 330px box, because the egg
 * is what stands proudest and reserving the box alone would put the status line
 * across the shell.
 */
const EGG_CROWN = 366;

/** The peak group's width. */
const PEAK_WIDTH = 520;

/** Honest about which ending this was, in the design's own voice. */
function headingFor(outcome: string): string {
  switch (outcome) {
    case "succeeded":
      return "It hatched, but empty";
    case "rejected":
      return "Every nest is taken";
    case "expired":
      return "The nest went cold";
    case "cancelled":
      return "You called it off";
    default:
      return "It didn't hatch";
  }
}
